import { createContext, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];
const API = process.env.REACT_APP_BACKEND_URL;

export const PLANS = [
  { key: "now", price: "$397", title: "Pay now", detail: "Charged today. Lowest founding price." },
  { key: "later", price: "$447", title: "Lock in, pay later", detail: "Save your card today — charged January 1, 2027." },
];

const ReserveContext = createContext({ open: () => {}, stage: null, setStage: () => {} });
export const useReserve = () => useContext(ReserveContext);

export const ReserveProvider = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [stage, setStage] = useState(null);
  const [source, setSource] = useState("hero");

  const open = (src = "cta") => {
    setSource(src);
    setOpen(true);
  };

  useEffect(() => {
    if (!window.__lenis) return;
    if (isOpen) window.__lenis.stop();
    else window.__lenis.start();
  }, [isOpen]);

  return (
    <ReserveContext.Provider value={{ open, stage, setStage }}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="reserve-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-ocean/80 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-cream border border-[var(--line)] p-7 sm:p-10 my-auto"
            >
              <button
                data-testid="reserve-modal-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="absolute top-4 right-5 font-sans text-2xl text-ocean/50 hover:text-ocean transition-colors"
              >
                ×
              </button>
              <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Launching January 5, 2027</span>
              <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-ocean tracking-tight leading-[1.02]">
                Reserve your <span className="italic text-cognac">Founding 50</span> spot.
              </h3>
              <p className="mt-4 font-sans text-sm text-ink/70 leading-relaxed">
                The first 50 founders join Brian Smith for a private live kickoff on launch day.
                Program price <span className="line-through text-ink/40">$497</span> — founding price from{" "}
                <span className="text-ocean font-medium">$397</span>.
              </p>
              <div className="mt-7">
                <ReserveForm source={source} dark={false} idPrefix="modal-reserve" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ReserveContext.Provider>
  );
};

export const ReserveForm = ({ source = "form", dark = true, idPrefix = "reserve" }) => {
  const { stage } = useReserve();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("now");
  const [status, setStatus] = useState("idle");
  const [existing, setExisting] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/api/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, plan, stage, source, origin_url: window.location.origin }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = Array.isArray(body.detail) ? body.detail[0]?.msg : body.detail;
        throw new Error(msg || "Please check your details and try again.");
      }
      if (body.already_reserved) {
        setExisting(body.reservation);
        setStatus("existing");
        return;
      }
      window.location.href = body.checkout_url;
    } catch (err) {
      setError(err.message);
      setStatus("idle");
    }
  };

  const text = dark ? "text-cream" : "text-ocean";
  const sub = dark ? "text-cream/65" : "text-ink/65";
  const field = dark
    ? "bg-transparent border-white/25 text-cream placeholder:text-cream/40 focus:border-cognac"
    : "bg-transparent border-[var(--line)] text-ocean placeholder:text-ink/40 focus:border-cognac";
  const planBase = dark ? "border-white/20 hover:border-cognac/70" : "border-[var(--line)] hover:border-cognac/70";
  const planActive = dark ? "border-cognac bg-white/[0.04]" : "border-cognac bg-sand";

  if (status === "existing" && existing) {
    return (
      <div data-testid={`${idPrefix}-existing`} className={`border-l-2 border-cognac pl-5 ${text}`}>
        <div className="font-sans text-xs uppercase tracking-[0.24em] text-cognac">Already reserved</div>
        <p className="mt-3 font-serif text-2xl sm:text-3xl leading-snug">
          {existing.name.split(" ")[0]}, your spot is already saved.
        </p>
        <p className={`mt-3 font-sans text-sm leading-relaxed ${sub}`}>
          {existing.founding_50 ? `You're founder #${existing.position} of the Founding 50.` : `You're #${existing.position} on the list.`}{" "}
          {existing.status === "card_saved"
            ? "Your card is on file and will be charged $447 on January 1, 2027."
            : "Your payment is complete. See you January 5."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          data-testid={`${idPrefix}-name-input`}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className={`w-full border px-4 py-3.5 font-sans text-sm outline-none transition-colors ${field}`}
        />
        <input
          data-testid={`${idPrefix}-email-input`}
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          className={`w-full border px-4 py-3.5 font-sans text-sm outline-none transition-colors ${field}`}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Payment option">
        {PLANS.map((p) => {
          const active = plan === p.key;
          return (
            <button
              type="button"
              key={p.key}
              role="radio"
              aria-checked={active}
              data-testid={`${idPrefix}-plan-${p.key}`}
              onClick={() => setPlan(p.key)}
              className={`text-left border px-4 py-4 transition-colors duration-300 ${active ? planActive : planBase}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className={`font-sans text-[11px] uppercase tracking-[0.18em] ${active ? "text-cognac" : sub}`}>{p.title}</span>
                <span className={`font-serif text-2xl leading-none ${text}`}>{p.price}</span>
              </div>
              <p className={`mt-2 font-sans text-xs leading-relaxed ${sub}`}>{p.detail}</p>
            </button>
          );
        })}
      </div>

      {stage && (
        <p data-testid={`${idPrefix}-stage-note`} className={`font-sans text-xs ${sub}`}>
          Your quiz result — <span className="text-cognac uppercase tracking-[0.16em]">{stage}</span> — will be saved with your reservation.
        </p>
      )}
      {error && (
        <p data-testid={`${idPrefix}-error`} className="font-sans text-sm text-red-400">{error}</p>
      )}
      <button
        data-testid={`${idPrefix}-submit-btn`}
        type="submit"
        disabled={status === "loading"}
        className="group w-full font-sans text-sm uppercase tracking-[0.18em] bg-cognac text-cream px-8 py-4 hover:bg-ocean disabled:opacity-60 transition-colors duration-400"
      >
        {status === "loading" ? "Taking you to secure checkout…" : plan === "now" ? "Reserve & Pay $397" : "Reserve & Save My Card"}
        <span className="hidden sm:inline-block ml-3 transition-transform duration-300 group-hover:translate-x-1">→</span>
      </button>
      <p className={`font-sans text-[11px] leading-relaxed ${sub}`}>
        {plan === "now"
          ? "Secure checkout by Stripe. Your Founding 50 seat is confirmed the moment payment completes."
          : "Secure checkout by Stripe. Nothing is charged today — your card is saved and billed $447 on January 1, 2027. Your seat is held as soon as the card is confirmed."}
      </p>
    </form>
  );
};
